import type { Note } from '@/types';
import { HitStatus } from '@/types';
import { useStorage } from '@vueuse/core';
import { computed, ref, watch, type InjectionKey, inject, provide } from 'vue';
import { useMetronome } from './useMetronome';
import type { SheetMusic } from './useSheetMusic';
import { findTimeInsertPositionInNotes } from '@/utils';
import { useSettings } from './useSettings';
import type AudioWaveform from '@/components/AudioWaveform.vue';
import { METRONOME_LOOKAHEAD_MS, HIT_WINDOW_MS, PERFECT_MS } from '@/constants';
import { useGlobalAudioContext } from './useGlobalAudioContext';
import type { LoopSource } from '@/types/classroom';

export const GAME_ENGINE_KEY = Symbol() as InjectionKey<ReturnType<typeof useGameEngine>>;

export function useInjectGameEngine() {
  const engine = inject(GAME_ENGINE_KEY);
  if (!engine) {
    throw new Error('useGameEngine must be provided via provide(GAME_ENGINE_KEY)');
  }
  return engine;
}

export function provideGameEngine(sheetMusic: SheetMusic) {
  const engine = useGameEngine(sheetMusic);
  provide(GAME_ENGINE_KEY, engine);
  return engine;
}

// Define game modes
export enum GameMode {
  SheetMusic = 'sheet_music',
  Arcade = 'arcade'
}

export type HighScoreDisqualifyReason = 'speed' | 'loop' | 'paused';

// Points a single note is worth, by hit status. Also used in reverse when a
// rewind takes back the hits of a passage that is about to be replayed.
const PERFECT_SCORE = 100;
const GOOD_SCORE = 50;

export function useGameEngine(sheetMusic: SheetMusic) {
  const metronome = useMetronome();
  const globalAudio = useGlobalAudioContext();

  const gameLoopId = ref<number>();
  const startTime = ref(0);
  const currentTime = ref(0);
  const currentGameNotes = ref<Note[]>([]);
  const nextPlayNoteIndex = ref(0);
  const isPlaying = ref(false);
  const previewMode = ref(false);
  // Whether the on-screen target line (the visual timing guide) is shown.
  // Hiding it is a self-imposed handicap — the player rewards themselves with
  // access to the S/S+ grade tiers (see getGradeForAccuracy). Not reset by
  // initGame: like previewMode, it's a standing preference across runs.
  const targetLineVisible = ref(true);
  // Tracks whether the target line was visible at ANY point during the
  // current run — reset (to mirror the current toggle) only on initGame, and
  // latched true for the rest of the run the moment it's shown. This is what
  // actually gates S/S+ eligibility, not the toggle's live value: switching
  // the line off partway through a run must not retroactively unlock S/S+ for
  // notes already played with it visible. Restarting the run is the only way
  // to clear the latch.
  const targetLineVisibleDuringRun = ref(targetLineVisible.value);
  // When true, preview mode auto-hits all notes as Perfect (admin-only feature).
  const autoPlayPerfect = ref(false);
  const lastMetronomeTickTime = ref(0);
  const hitNotes = ref(0);
  const score = ref(0);
  const gameMode = useStorage<GameMode>('gameMode', GameMode.SheetMusic);
  const settings = useSettings();
  const metronomeVolume = useStorage('metronome-volume', 1.0);
  const lastHitStatus = ref<HitStatus>();
  const hitAnimationTrigger = ref(0);
  
  // Loop section state
  const loopStartMeasure = ref<number | null>(null);
  const loopEndMeasure = ref<number | null>(null);
  const loopSource = ref<LoopSource>();
  const loopEnabled = computed(() => loopSource.value !== undefined);
  // True replays the measure before the loop start on every pass; false gives it
  // only to the initial launch. Host-controlled in a classroom (broadcast through
  // room state) because each client runs its own seam.
  const loopPreRollEveryPass = ref(true);
  // Range + source held across a toolbar loop disable, so re-enabling resumes the
  // same section instead of sending the user back to the selector. Distinct from
  // clearLoopSection, which is the user discarding the section outright.
  const suspendedLoop = ref<{
    startMeasure: number;
    endMeasure: number;
    source: Exclude<LoopSource, undefined>;
  } | null>(null);
  // Whether the engine holds a section the toggle can switch back on.
  const hasLoopSelection = computed(() => loopEnabled.value || suspendedLoop.value !== null);
  // Edge-trigger for the loop seam: the source is repositioned once per pass,
  // not on every frame the clock spends inside the seam window.
  const loopSeamArmed = ref(false);
  
  // Combo tracking
  const comboCount = ref(0);
  const longestCombo = ref(0);
  // Stats for game results
  const perfectCount = ref(0);
  const goodCount = ref(0);
  const missedCount = ref(0);
  // Audio duration (set externally if audio is available)
  const audioDuration = ref<number>();
  // Audio offset and waveform (for audio playback)
  const audioOffset = ref(0);
  const audioWaveform = ref<InstanceType<typeof AudioWaveform>>();

  // Playback speed (per-session). Range: [0.5, 1.0]. Scales the game clock so
  // sheet music, metronome, MIDI hit detection, and the audio backing track
  // all slow down together while preserving pitch via WaveSurfer.
  const playbackSpeed = ref(1.0);

  // Highscore eligibility — a set of reasons the current run won't count
  // toward the user's stored highscore. Cleared on initGame.
  // - 'speed':  playback ran below 1.0 at any point
  // - 'loop':   a section/custom loop was active at any point
  // - 'paused': the user paused playback partway through the run
  const highscoreDisqualifyReasons = ref<Set<HighScoreDisqualifyReason>>(new Set());
  const isHighscoreEligible = computed(() => highscoreDisqualifyReasons.value.size === 0);
  const addHighscoreDisqualifyReason = (reason: HighScoreDisqualifyReason) => {
    if (highscoreDisqualifyReasons.value.has(reason)) return;
    const next = new Set(highscoreDisqualifyReasons.value);
    next.add(reason);
    highscoreDisqualifyReasons.value = next;
  };

  // Desync tracking and correction
  const lastDesyncLogTime = ref(0);
  const desyncThresholdMs = ref(35); // correct if drift exceeds this

  const totalNotes = computed(() => sheetMusic.notes.value.length);
  const accuracy = computed(() => totalNotes.value > 0 ? (hitNotes.value / totalNotes.value) * 100 : 0);

  // Compute loop boundaries from measure start boundaries, not arbitrary note buffers.
  const loopStartTime = computed(() => {
    if (loopStartMeasure.value === null) return null;
    return sheetMusic.getMeasureStartMs(loopStartMeasure.value) ?? null;
  });

  const loopEndTime = computed(() => {
    if (loopEndMeasure.value === null) return null;

    // End boundary is the start of the next measure.
    const nextMeasureStart = sheetMusic.getMeasureStartMs(loopEndMeasure.value + 1) ?? null;
    if (nextMeasureStart !== null) {
      return Math.min(nextMeasureStart, sheetMusic.songDuration.value);
    }

    // Fallback: derive from end measure start + estimated measure duration.
    const endMeasureStart = sheetMusic.getMeasureStartMs(loopEndMeasure.value) ?? null;
    if (endMeasureStart !== null) {
      const durationMs = sheetMusic.getMeasureDurationMs(loopEndMeasure.value);
      if (durationMs !== undefined) {
        return Math.min(endMeasureStart + durationMs, sheetMusic.songDuration.value);
      }
    }

    return sheetMusic.songDuration.value > 0 ? sheetMusic.songDuration.value : null;
  });

  // Calculate progress as a percentage based on the sheet music duration.
  const progress = computed(() => {
    if (sheetMusic.songDuration.value <= 0) return 0;
    return Math.min(((currentTime.value ?? 0) + METRONOME_LOOKAHEAD_MS) / (sheetMusic.songDuration.value + METRONOME_LOOKAHEAD_MS) * 100, 100);
  });

  const getAudioCurrentTimeMs = (): number | undefined => {
    if (!audioWaveform.value) return;
    // Align to absolute timeline used by game (add configured offset)
    return audioWaveform.value.getCurrentTimeInMs() + (audioOffset.value || 0);
  };

  const realignClockToAudio = (audioNowMs: number) => {
    // Set game clock to audio timeline so visuals and logic match. The game
    // clock advances at `playbackSpeed * realElapsed`, so to make
    // `currentTime === audioNowMs` we offset startTime by `audioNowMs / speed`.
    startTime.value = performance.now() - audioNowMs / playbackSpeed.value;
  };

  // Notes sharing one drawn notehead, keyed by the graphical note they share — a
  // tremolo's strokes are the only group larger than one. Built over
  // currentGameNotes, not sheetMusic.notes: those are fresh copies each run, and a
  // group holding the originals would never see a hit land.
  const noteheadGroups = new Map<unknown, Note[]>();

  const rebuildNoteheadGroups = () => {
    noteheadGroups.clear();
    for (const note of currentGameNotes.value) {
      if (!note.graphicalNote) continue;
      const group = noteheadGroups.get(note.graphicalNote);
      if (group) group.push(note);
      else noteheadGroups.set(note.graphicalNote, [note]);
    }
  };

  // A note with no graphical note is never keyed in, so it falls back to standing alone.
  const noteheadGroupOf = (note: Note): Note[] =>
    noteheadGroups.get(note.graphicalNote) ?? [note];

  // Repaint every notehead the given notes sit on, once each. Notes that were just
  // reset to Unplayed settle back to the plain notehead, so this covers clearing the
  // board as well as reflecting a hit — there is no separate reset-to-base path.
  const repaintNoteheads = (notes: Note[]) => {
    const done = new Set<unknown>();
    for (const note of notes) {
      if (!note.graphicalNote || done.has(note.graphicalNote)) continue;
      done.add(note.graphicalNote);
      sheetMusic.colorNoteheadByStatus(noteheadGroupOf(note));
    }
  };

  const resetNoteHitHistory = () => {
    currentGameNotes.value.forEach(note => {
      note.played = false;
      note.hitStatus = HitStatus.Unplayed;
      note.timingOffset = undefined;
    });
    repaintNoteheads(currentGameNotes.value);
    score.value = 0;
    hitNotes.value = 0;
    perfectCount.value = 0;
    goodCount.value = 0;
    missedCount.value = 0;
    comboCount.value = 0;
    longestCombo.value = 0;
    lastHitStatus.value = undefined;
    hitAnimationTrigger.value = 0;
  };

  // Rewinds the run to `timeMs`: notes at/after it go back to unplayed and the
  // score and counters they contributed are handed back, so replaying that
  // stretch scores it once instead of stacking onto the previous pass. Combo is
  // deliberately left alone — a rewind doesn't undo the streak already built.
  const revertNoteHitsAtOrAfter = (timeMs: number) => {
    currentGameNotes.value.forEach(note => {
      if (note.timestamp < timeMs) return;

      switch (note.hitStatus) {
        case HitStatus.Perfect:
          hitNotes.value--;
          perfectCount.value--;
          score.value -= PERFECT_SCORE;
          break;
        case HitStatus.Good:
          hitNotes.value--;
          goodCount.value--;
          score.value -= GOOD_SCORE;
          break;
        case HitStatus.Missed:
          missedCount.value--;
          break;
      }

      note.played = false;
      note.hitStatus = HitStatus.Unplayed;
      note.timingOffset = undefined;
    });
    // Repaint by group rather than blanket-resetting to base: a roll straddling
    // the rewind point keeps the strokes before it, so its notehead has to go back
    // to whatever those now settle on — which may still be a miss.
    repaintNoteheads(currentGameNotes.value.filter(note => note.timestamp >= timeMs));
  };

  // Find notes with a specific timestamp
  // Returns notes with the exact timestamp, leveraging the fact that notes are sorted
  const findNotesWithTimestamp = (timestamp: number): Note[] => {
    // Start searching from an index near nextPlayNoteIndex
    // We use a small offset to account for notes that might have been missed
    const notes: Note[] = [];

    // Find the first note with our timestamp
    const startIdx = nextPlayNoteIndex.value;
    const note = currentGameNotes.value[startIdx];
    if (note.timestamp > timestamp) {
      throw new Error("findNotesWithTimestamp timestamp is prior to nextPlayNote timestamp");
    }

    // If we didn't find any notes with this timestamp, return empty array
    if (startIdx === -1) {
      return notes;
    }

    // Collect all notes with the target timestamp
    // Start from the first note we found and continue until the timestamp changes
    for (let i = startIdx; i < currentGameNotes.value.length; i++) {
      const note = currentGameNotes.value[i];
      if (Math.round(note.timestamp) === Math.round(timestamp)) {
        notes.push(note);
      } else if (Math.round(note.timestamp) > Math.round(timestamp)) {
        // We've reached notes with a different timestamp, stop collecting
        break;
      }
    }

    return notes;
  };

  // Initialize game
  const initGame = () => {
    stopPlayback();

    score.value = 0;
    hitNotes.value = 0;
    currentTime.value = -METRONOME_LOOKAHEAD_MS;
    lastMetronomeTickTime.value = -1;

    // Reset combo tracking
    comboCount.value = 0;
    longestCombo.value = 0;
    // Reset note stats
    perfectCount.value = 0;
    goodCount.value = 0;
    missedCount.value = 0;

    isPlaying.value = false;

    // Fresh run: clear disqualifications so a new attempt is eligible by default.
    // Re-evaluate against current speed/loop state so a run started already
    // slow or with loop on is correctly flagged from the start.
    highscoreDisqualifyReasons.value = new Set();
    if (playbackSpeed.value !== 1) addHighscoreDisqualifyReason('speed');
    if (loopEnabled.value) addHighscoreDisqualifyReason('loop');

    // Fresh run: the latch starts wherever the toggle currently sits, then
    // only ever flips true (see the watcher near targetLineVisible's
    // declaration above).
    targetLineVisibleDuringRun.value = targetLineVisible.value;

    currentGameNotes.value = sheetMusic.notes.value.map(note => ({
      ...note,
      played: false,
      hitStatus: HitStatus.Unplayed,
      timingOffset: undefined
    }));

    rebuildNoteheadGroups();
    repaintNoteheads(currentGameNotes.value);
    nextPlayNoteIndex.value = 0;
    lastHitStatus.value = undefined;
    hitAnimationTrigger.value = 0;
  };

  // Wall-clock ms that a launch parks the game clock behind `clockTimeMs`.
  // Mid-track resume at slowed tempo: SoundTouch's WSOLA buffer delays the
  // first audible sample by this much, so the clock is pre-delayed to match and
  // visuals/metronome land on it. The hold then persists for the session —
  // trackAndCorrectDesync reconciles the clock against the *audible* position,
  // which carries the same delay, so it has nothing to correct. Zero at rate 1
  // (worklet bypassed) and on the count-in path, where schedulePlay fires
  // wavesurfer.play() early to absorb the latency inside the count-in instead.
  const getLaunchLatencyHoldMs = (clockTimeMs: number) => {
    if (!audioWaveform.value) return 0;
    if (clockTimeMs - audioOffset.value <= 0) return 0;
    return audioWaveform.value.getProcessingLatencyMs();
  };

  // The same hold in song ms, keyed on the song position a launch starts from
  // rather than the clock position it lands on. For comparing this clock against
  // an external timeline that knows nothing about our audio pipeline — a
  // synchronized room projects an ideal timeline, and every client launching from
  // the same position sits this far behind it. Subtract before reading drift.
  const getLaunchLatencyHoldSongMs = (songTimeMs: number) =>
    getLaunchLatencyHoldMs(songTimeMs - METRONOME_LOOKAHEAD_MS) * playbackSpeed.value;

  // Start the game
  const startGame = async () => {
    await globalAudio.ensureReadyFromUserGesture();
    if (gameLoopId.value != null) return;

    // If looping is enabled, start from one measure before loop start when possible.
    if (loopEnabled.value && loopStartTime.value !== null && loopEndTime.value !== null) {
      jumpToLoopPreRollStart();
    }

    const audioTimeMs = audioWaveform.value ? currentTime.value - audioOffset.value : null;
    const latencyHoldMs = getLaunchLatencyHoldMs(currentTime.value);

    // Update startTime to maintain the current position. The game clock
    // formula is `currentTime = (now - startTime) * playbackSpeed`, so
    // anchoring at the current position requires dividing by speed.
    startTime.value = performance.now() + latencyHoldMs - currentTime.value / playbackSpeed.value;
    isPlaying.value = true;

    // Reset metronome phase for this play session.
    lastMetronomeTickTime.value = -1;

    // Start audio playback if available
    if (audioWaveform.value && audioTimeMs != null) {
      if (audioTimeMs > 0) {
        // Audio starts before count-in finishes (negative offset is larger than count-in)
        audioWaveform.value.play(audioTimeMs);
      } else {
        // Audio starts after count-in. The remaining game-time delay is
        // `-audioTimeMs`; convert to real time by dividing by speed because
        // setTimeout runs in real time but our delay is expressed in game ms.
        audioWaveform.value.schedulePlay(-audioTimeMs / playbackSpeed.value);
      }
    }

    gameLoopId.value = requestAnimationFrame(gameLoop);
  };

  // Stop the game
  const stopPlayback = () => {
    if (!isPlaying.value) return;

    // Pausing before the run finishes disqualifies it from setting a highscore.
    // gameLoop's auto-stop runs after `sheetMusic.songDuration + 1000`, so natural
    // end-of-song stops fall outside this gate.
    if (currentTime.value < sheetMusic.songDuration.value) {
      addHighscoreDisqualifyReason('paused');
    }

    isPlaying.value = false;
    // Pausing mid-seam abandons it; the resume path seeks and re-arms.
    loopSeamArmed.value = false;

    // Stop animation loop
    if (gameLoopId.value) {
      cancelAnimationFrame(gameLoopId.value);
      gameLoopId.value = undefined;
    }

    // Stop metronome
    metronome.stop();
  };

  // Game timeline → audio timeline. The backing track is shifted by the
  // user-calibrated offset, so a negative result means the clock hasn't reached
  // the track's start yet — repositionAudio holds it silent rather than seeking.
  const toAudioTimeMs = (gameTimeMs: number) => gameTimeMs - audioOffset.value;

  // Point the backing track at game time `timeMs`, in whichever way that position
  // calls for. Before the track's own start there is nothing to seek to: the
  // track has to wait out the offset, or a loop back into the lead-in would hear
  // it fire early on every pass. `gapless` lets a loop seam's tail keep draining
  // (see the two-stage seam in gameLoop); every other reposition is a
  // discontinuous jump and flushes.
  const repositionAudio = (timeMs: number, gapless = false) => {
    const waveform = audioWaveform.value;
    if (!waveform) return;

    const audioTimeMs = toAudioTimeMs(timeMs);
    if (audioTimeMs < 0) {
      // setTimeout runs in real time, so the game-ms wait is divided by speed.
      // While paused there is nothing to hold back: park the source at its start
      // and let the launch schedule from wherever the clock finally sits.
      if (isPlaying.value) waveform.schedulePlay(-audioTimeMs / playbackSpeed.value);
      else waveform.seekToTime(0);
      return;
    }

    if (gapless) waveform.seekSourceForLoopSeam(audioTimeMs);
    else waveform.seekToTime(audioTimeMs);
  };

  // Move the clock and hand back hits at/after `timeMs`, so a replayed stretch
  // scores once instead of stacking onto the previous pass. Leaves the audio
  // alone — callers either move it themselves or, at a loop seam, have already
  // repositioned the source ahead of the wrap.
  const rewindClockAndNotes = (timeMs: number, preRollMetronome = true) => {
    revertNoteHitsAtOrAfter(timeMs);
    seekTo(timeMs, preRollMetronome);
  };

  // Move clock, notes and audio together, in either direction. Repositioning
  // that has to survive must go through here: `seekTo` moves the clock alone,
  // and trackAndCorrectDesync realigns it straight back to the audio playhead it
  // left behind — undoing the seek a frame later.
  const seekWithAudio = (timeMs: number) => {
    rewindClockAndNotes(timeMs);
    repositionAudio(timeMs);
  };

  const getLoopPreRollStartMs = (): number | null => {
    if (!loopEnabled.value || loopStartTime.value === null || loopStartMeasure.value === null) return null;
    if (loopStartMeasure.value <= 0) return 0;
    return sheetMusic.getMeasureStartMs(loopStartMeasure.value - 1) ?? 0;
  };

  const jumpToLoopPreRollStart = () => {
    const preRollStartMs = getLoopPreRollStartMs();
    if (preRollStartMs === null) return;
    seekWithAudio(preRollStartMs);
  };

  // Where a loop pass restarts. Only the lead-in mode separates it from the loop
  // start; the initial launch always gets the pre-roll (see startGame).
  const loopWrapTime = computed(() => {
    if (loopStartTime.value === null) return null;
    if (!loopPreRollEveryPass.value) return loopStartTime.value;
    return getLoopPreRollStartMs() ?? loopStartTime.value;
  });

  // Game loop - called every animation frame
  const gameLoop = () => {
    if (!isPlaying.value) return;

    // Calculate current game time. Real elapsed time is scaled by playbackSpeed
    // so the game clock advances at the same rate WaveSurfer's audio position
    // advances when `setPlaybackRate(playbackSpeed, true)` is in effect — this
    // keeps audio and visuals in sync at any speed.
    currentTime.value = (performance.now() - startTime.value) * playbackSpeed.value;

    // Loop seam in two stages: reposition the audio source one WSOLA window early
    // (letting the loop's tail keep draining), then wrap the game clock at the
    // exact boundary. Splitting them keeps the audio gapless (no flush = no gap)
    // while the beat grid stays exact (clock wraps on the boundary).
    if (loopEnabled.value && loopEndTime.value !== null && loopWrapTime.value !== null) {
      // The early stage only earns its keep when there is a source to reposition.
      // A wrap that lands before the track's start pauses instead, and pausing a
      // window early would clip the loop's tail — so act on the boundary itself.
      const seamLeadMs = toAudioTimeMs(loopWrapTime.value) >= 0
        ? (audioWaveform.value?.getLatencyInSourceMs() ?? 0)
        : 0;
      if (!loopSeamArmed.value && currentTime.value >= loopEndTime.value - seamLeadMs) {
        loopSeamArmed.value = true;
        repositionAudio(loopWrapTime.value, true);
      }
      if (currentTime.value >= loopEndTime.value) {
        // Clock and notes only — the audio is already streaming the wrap target.
        rewindClockAndNotes(loopWrapTime.value, false);
        gameLoopId.value = requestAnimationFrame(gameLoop);
        return;
      }
    }

    // Synchronize metronome with audio time when available
    synchronizeMetronome();

    // Monitor desync and correct
    trackAndCorrectDesync();

    processCurrentNotes();

    // Check for end of song (only if not looping)
    // Only stop the game after all notes have been processed AND a cooldown period (1 seconds)
    if (currentTime.value > sheetMusic.songDuration.value + 1000 && !loopEnabled.value) {
      stopPlayback();
      return;
    }

    // Continue the loop
    gameLoopId.value = requestAnimationFrame(gameLoop);
  };

  // Synchronize metronome with audio-driven time
  const synchronizeMetronome = () => {
    // The lookahead compensates for audio scheduling latency, which is a
    // real-time phenomenon. At slower playback speeds, the same number of
    // real-time milliseconds covers fewer game-time milliseconds, so scale.
    const lookAHeadMilliseconds = METRONOME_LOOKAHEAD_MS * playbackSpeed.value;
    const targetMetronomeTime = currentTime.value + lookAHeadMilliseconds;
    const beatInfo = sheetMusic.getBeatAtTime(targetMetronomeTime);
    if (!beatInfo) return;

    if (lastMetronomeTickTime.value < beatInfo.beatStartMs) {
      const isAccent = beatInfo.beatIndexInMeasure === 0;
      metronome.tick(isAccent, metronomeVolume.value);
      lastMetronomeTickTime.value = beatInfo.beatStartMs;
    }
  };

  // Periodically log desync and correct drift by aligning to audio timeline
  const trackAndCorrectDesync = () => {
    // Mid-seam the source already reads from the loop start while the clock is
    // still playing out the tail, so the two disagree by design. Realigning here
    // would drag the clock back and break the beat grid.
    if (loopSeamArmed.value) return;

    const audioNow = getAudioCurrentTimeMs();
    // Stop realigning once we're near the audio's end. At low playback speeds
    // the audio's reported currentTime can stall a few hundred ms shy of file
    // duration; continued realignment then pins the game clock below
    // sheetMusic.songDuration and the gameLoop's natural stop condition never fires.
    const END_EPSILON_MS = 200;
    if (audioNow == null
      || audioDuration.value == null
      || audioNow >= audioDuration.value - END_EPSILON_MS) return;

    const desyncMs = Math.round(audioNow - currentTime.value);

    // Log at most every 250ms
    const nowTs = performance.now();
    if (nowTs - lastDesyncLogTime.value >= 250) {
      // Positive desync means game/metronome are behind the audio
      // console.log('[Sync] audioNow:', audioNow, 'gameNow:', currentTime.value, 'desyncMs:', desyncMs, 'targetOffset:', targetOffsetMs);
      lastDesyncLogTime.value = nowTs;
    }

    // If drift exceeds threshold, realign the game clock and metronome phase
    if (Math.abs(desyncMs) > desyncThresholdMs.value) {
      realignClockToAudio(audioNow);
    }
  };

  // Process notes for the current frame
  const processCurrentNotes = () => {
    if (!currentGameNotes.value.length) return;

    // In preview mode, the piece plays through without scoring or note coloring.
    // We still need to advance nextPlayNoteIndex so progress / loop logic stays correct.
    if (previewMode.value) {
      while (
        nextPlayNoteIndex.value < currentGameNotes.value.length &&
        currentTime.value > currentGameNotes.value[nextPlayNoteIndex.value].timestamp + HIT_WINDOW_MS
      ) {
        nextPlayNoteIndex.value++;
      }
      return;
    }

    // Admin auto-play: automatically hit all notes with Perfect timing.
    if (autoPlayPerfect.value) {
      while (
        nextPlayNoteIndex.value < currentGameNotes.value.length &&
        currentTime.value >= currentGameNotes.value[nextPlayNoteIndex.value].timestamp
      ) {
        const note = currentGameNotes.value[nextPlayNoteIndex.value];
        if (!note.played) {
          note.played = true;
          note.hitStatus = HitStatus.Perfect;
          note.timingOffset = 0;
          hitNotes.value++;
          perfectCount.value++;
          score.value += PERFECT_SCORE;
          comboCount.value++;
          if (comboCount.value > longestCombo.value) {
            longestCombo.value = comboCount.value;
          }
          sheetMusic.colorNoteheadByStatus(noteheadGroupOf(note));
          triggerHitAnimation(HitStatus.Perfect);
        }
        nextPlayNoteIndex.value++;
      }
      return;
    }

    // Process only the current and next few notes rather than looping through all remaining notes
    const maxNotesToCheck = 5; // Check at most 5 notes ahead for efficiency
    const endIndex = Math.min(nextPlayNoteIndex.value + maxNotesToCheck, currentGameNotes.value.length);
    let hasNewlyMissedNotes = false;

    for (let i = nextPlayNoteIndex.value; i < endIndex; i++) {
      const note = currentGameNotes.value[i];

      // If we've passed this note's time completely
      if (currentTime.value > note.timestamp + HIT_WINDOW_MS) {
        if (!note.played) {
          // Mark as missed note
          note.hitStatus = HitStatus.Missed;
          sheetMusic.colorNoteheadByStatus(noteheadGroupOf(note));
          hasNewlyMissedNotes = true;
          missedCount.value++;
        }
        nextPlayNoteIndex.value = i + 1;
      }

      // If the note is still in the future, stop checking
      if (note.timestamp > currentTime.value + HIT_WINDOW_MS) {
        break;
      }
    }

    // If we have any newly missed notes, trigger the missed animation and reset combo
    if (hasNewlyMissedNotes) {
      triggerHitAnimation(HitStatus.Missed);
      comboCount.value = 0;
    }
  };

  function registerHit(noteTime: number, hitTime: number) {
    // Calculate timing accuracy
    const timeDifference = Math.abs(noteTime - hitTime);

    if (timeDifference <= HIT_WINDOW_MS) {
      hitNotes.value++;

      // Determine hit status
      const hitStatus = timeDifference <= PERFECT_MS ? HitStatus.Perfect : HitStatus.Good;

      // Score and count by hit status
      if (hitStatus === HitStatus.Perfect) {
        score.value += PERFECT_SCORE;
        perfectCount.value++;
      } else {
        score.value += GOOD_SCORE;
        goodCount.value++;
      }

      // Increment combo counter and update longest combo
      comboCount.value++;
      if (comboCount.value > longestCombo.value) {
        longestCombo.value = comboCount.value;
      }

      return {
        hitStatus,
        timingOffset: hitTime - noteTime
      };
    }
  }

  // Check if a player's input hit a note
  const checkNoteHit = (hitTime: number, midiNote: number) => {
    // In preview mode we ignore all hit detection so the piece plays through cleanly.
    if (previewMode.value) return;

    // Apply user-configured input calibration before mapping to game time. The
    // offset is a real-time correction (input/audio latency), so subtract from
    // the raw `performance.now()` timestamp before scaling by playbackSpeed.
    const calibratedHitTime = hitTime - settings.midiCalibrationOffsetMs.value;

    // Calculate the hit time relative to the game start. `hitTime` comes from
    // `performance.now()` at the MIDI event (real time), so we scale by
    // playbackSpeed to land it on the slowed game timeline. As a side effect,
    // the HIT_WINDOW_MS hit window stays fixed in *game* time, which becomes a
    // wider hit window in real time at slow speeds — natural for slow practice.
    const hitTimeRelativeToStart = (calibratedHitTime - (startTime.value || 0)) * playbackSpeed.value;

    // Collect potential timestamps to check based on the hit time
    const potentialTimestamps = new Set<number>();

    // Process only the current and next few notes ahead, similar to processCurrentNotes
    const maxNotesToCheck = 10; // Check at most 10 notes ahead for efficiency
    const endIndex = Math.min(nextPlayNoteIndex.value + maxNotesToCheck, currentGameNotes.value.length);

    for (let i = nextPlayNoteIndex.value; i < endIndex; i++) {
      const note = currentGameNotes.value[i];

      // If the note's timestamp is within the hit window, consider it
      if (Math.abs(note.timestamp - hitTimeRelativeToStart) <= HIT_WINDOW_MS) {
        potentialTimestamps.add(note.timestamp);
      }

      // If the note is too far in the future, stop checking (similar to processCurrentNotes)
      if (note.timestamp > hitTimeRelativeToStart + HIT_WINDOW_MS) {
        break;
      }
    }

    // Find all matching unplayed notes that match the MIDI note
    const matchingNotes: Note[] = [];

    // Check all potential timestamps
    for (const timestamp of potentialTimestamps) {
      // Get all notes at this timestamp
      const notesAtTimestamp = findNotesWithTimestamp(timestamp);

      // Filter for unplayed notes that match the MIDI note
      for (const note of notesAtTimestamp) {
        if (!note.played && note.possibleMidiNumbers.includes(midiNote)) {
          matchingNotes.push(note);
        }
      }
    }

    // Sort by timing accuracy (closest first)
    matchingNotes.sort((a, b) => {
      return Math.abs(a.timestamp - hitTimeRelativeToStart) - Math.abs(b.timestamp - hitTimeRelativeToStart);
    });

    // If we found at least one matching note
    if (matchingNotes.length > 0) {
      // Take the closest note by timing
      const closestNote = matchingNotes[0];
      closestNote.played = true;
      const timingOffset = hitTimeRelativeToStart - closestNote.timestamp;

      // Debug info about the hit note
      // +ve timing offset means too late
      // -ve timing offset means too early
      // console.log('Hit note:', closestNote.noteName, 'at timing offset:', timingOffset, 'ms');

      // Register the hit with the game store
      const hitResult = registerHit(closestNote.timestamp, hitTimeRelativeToStart);
      if (hitResult) {
        // Update the note with hit status and timing information
        closestNote.hitStatus = hitResult.hitStatus;
        closestNote.timingOffset = hitResult.timingOffset;

        // Repaint the notehead this note sits on. For a tremolo that is the whole
        // roll's, so the group decides the color, not just the stroke that landed.
        sheetMusic.colorNoteheadByStatus(noteheadGroupOf(closestNote));

        // Check if all notes at this timestamp have been played
        // and determine the worst hit status among them
        checkAndTriggerHitAnimationForTimestamp(closestNote.timestamp);
      }
    } else {
      console.log('No matching notes found for MIDI note', midiNote);
    }
  };

  // Check if all notes at a timestamp have been played and trigger hit animation based on worst status
  function checkAndTriggerHitAnimationForTimestamp(timestamp: number) {
    // Get all notes with the specified timestamp
    const notesAtTimestamp = findNotesWithTimestamp(timestamp);

    // Check if all notes at this timestamp have been played
    const allNotesPlayed = notesAtTimestamp.every(note => note.played);

    // Only trigger animation if all notes at this timestamp have been played
    if (allNotesPlayed) {
      // If any note is missed at this timestamp, don't show animation
      const anyMissedNotes = notesAtTimestamp.some(note => note.hitStatus === HitStatus.Missed);
      if (anyMissedNotes) {
        return;
      }

      // Find the worst hit status among all played notes at this timestamp
      // Perfect > Good (we consider Good worse than Perfect)
      const hasGoodHits = notesAtTimestamp.some(note => note.hitStatus === HitStatus.Good);

      // Trigger animation for the worst hit status (Good takes precedence over Perfect)
      if (hasGoodHits) {
        triggerHitAnimation(HitStatus.Good);
      } else {
        // If all are perfect
        triggerHitAnimation(HitStatus.Perfect);
      }
    }
  }

  function triggerHitAnimation(status: HitStatus) {
    lastHitStatus.value = status;
    hitAnimationTrigger.value++;
  }

  // Handle seeking to a specific time position. `preRollMetronome` backdates the
  // clock by one lookahead window so the beat at `targetTime` still gets cued —
  // the metronome schedules that far ahead and the delay absorbs it. A loop wrap
  // is already mid-stream and on the beat, so it passes false: backdating there
  // puts the clock 50ms before the loop start and visibly jumps the cursor back.
  const seekTo = (targetTime: number, preRollMetronome = true) => {
    // Any seek ends the seam: either it was the wrap itself, or it superseded a
    // pending one. Re-arm so the next pass repositions the source again.
    loopSeamArmed.value = false;
    currentTime.value = preRollMetronome
      ? targetTime - METRONOME_LOOKAHEAD_MS
      : targetTime;
    // Anchor startTime so the next gameLoop tick produces currentTime ≈ targetTime
    // under `currentTime = (now - startTime) * playbackSpeed`.
    startTime.value = performance.now() - targetTime / playbackSpeed.value;

    // Use binary search to find the next note index - O(log n)
    const insertPosition = findTimeInsertPositionInNotes(currentGameNotes.value, targetTime);

    // Set the next note index to the insertion position
    nextPlayNoteIndex.value = Math.max(0, insertPosition);

    const beatInfo = sheetMusic.getBeatAtTime(targetTime);
    lastMetronomeTickTime.value = beatInfo?.beatStartMs ?? -1;
  };

  // Loop section management
  const setLoopSection = (
    startMeasure: number,
    endMeasure: number,
    enabled = true,
    source?: Exclude<LoopSource, undefined>
  ) => {
    if (!enabled) {
      clearLoopSection();
      return;
    }

    loopStartMeasure.value = startMeasure;
    loopEndMeasure.value = endMeasure;
    if (source !== undefined) {
      loopSource.value = source;
    } else if (loopSource.value === undefined) {
      loopSource.value = 'custom';
    }
    // A freshly chosen section supersedes whatever the toggle was holding.
    suspendedLoop.value = null;

    jumpToLoopPreRollStart();
  };

  const clearLoopSection = () => {
    loopStartMeasure.value = null;
    loopEndMeasure.value = null;
    loopSource.value = undefined;
    suspendedLoop.value = null;
  };

  // Toolbar loop toggle. Disabling parks the range so re-enabling resumes it;
  // clearLoopSection is the discard path and deliberately drops that memory.
  const setLoopEnabled = (enabled: boolean) => {
    if (enabled === loopEnabled.value) return;

    if (!enabled) {
      if (loopStartMeasure.value === null || loopEndMeasure.value === null || loopSource.value === undefined) return;
      const parked = {
        startMeasure: loopStartMeasure.value,
        endMeasure: loopEndMeasure.value,
        source: loopSource.value,
      };
      clearLoopSection();
      suspendedLoop.value = parked;
      return;
    }

    const resumed = suspendedLoop.value;
    if (!resumed) return;
    setLoopSection(resumed.startMeasure, resumed.endMeasure, true, resumed.source);
  };

  // Watch for state changes that disqualify the current run from a highscore.
  // Reasons are sticky for the run — they only clear on initGame.
  watch(playbackSpeed, (speed) => {
    if (speed !== 1) addHighscoreDisqualifyReason('speed');
  });
  watch(loopEnabled, (enabled) => {
    if (enabled) addHighscoreDisqualifyReason('loop');
  });
  // Latch: once the target line is shown during a run, it stays "was
  // visible" for the rest of that run (see targetLineVisibleDuringRun above).
  watch(targetLineVisible, (visible) => {
    if (visible) targetLineVisibleDuringRun.value = true;
  });

  watch(
    [currentTime, () => sheetMusic.measureTimingMap.value.length],
    ([newTime]) => {
      const segment = sheetMusic.getMeasureAtTime(newTime);
      sheetMusic.currentMeasureIndex.value = segment?.measureIndex ?? 0;
    }
  );

  return {
    currentTime,
    isPlaying,
    previewMode,
    targetLineVisible,
    targetLineVisibleDuringRun,
    score,
    accuracy,
    progress,
    audioDuration,
    audioOffset,
    audioWaveform,
    playbackSpeed,
    getLaunchLatencyHoldSongMs,
    gameMode,
    comboCount,
    longestCombo,
    perfectCount,
    goodCount,
    missedCount,
    hitNotes,
    totalNotes,
    hitAnimationTrigger,
    currentGameNotes,
    lastHitStatus,
    startTime,
    lastMetronomeTickTime,
    loopStartMeasure,
    loopEndMeasure,
    loopEnabled,
    loopSource,
    loopStartTime,
    loopEndTime,
    loopPreRollEveryPass,
    hasLoopSelection,
    getLoopPreRollStartMs,
    setLoopSection,
    setLoopEnabled,
    clearLoopSection,
    initGame,
    startGame,
    stopPlayback,
    resetNoteHitHistory,
    revertNoteHitsAtOrAfter,
    checkNoteHit,
    seekTo,
    seekWithAudio,
    highscoreDisqualifyReasons,
    isHighscoreEligible,
    autoPlayPerfect,
  };
} 
